import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { useSessionMutation } from "@/lib/query/core/use-session-mutation";
import { FirstLoginPasswordError, replaceFirstLoginPassword } from "@/lib/api/client";
import { markFirstPasswordNeedsCurrent } from "@/modules/auth/lib/first-visit";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { changePrivateSession } from "@/shared/services/privateSessionChange";
import { clearPasswordSavedNotice, markPasswordSavedNotice } from "@/lib/auth/password-saved-notice";
export function useReplaceFirstLoginPasswordMutation() {
  const client = useQueryClient();
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  const { data: replacementFailed } = useQuery<boolean>({
    queryKey: ["first-password-feedback", session.generation],
    queryFn: () => false, enabled: false,
  });
  const mutation = useSessionMutation({
    retry: false, meta: { skipGlobalErrorToast: true },
    mutationFn: async (input: { currentPassword?: string; nextPassword: string }) => {
      const owner = getPrivateSessionState();
      const generation = owner.generation + 1;
      client.removeQueries({ queryKey: ["first-password-feedback"] });
      try {
        return await changePrivateSession(client, async generation => {
          const result = await replaceFirstLoginPassword(generation, input.currentPassword, input.nextPassword);
          // A backend that re-issued the session on this response keeps the
          // seller signed in (A6). An older one rotated her out: sign out
          // cleanly so the login page starts from nothing.
          if (!result?.redirectPath) {
            await apiJsonClient(endpoints.seller.auth.logout, { method: "POST" });
          } else {
            // Left BEFORE the rebind: the page that asked for "saved" does not
            // survive it; the locale layout shows the toast on landing (B7).
            markPasswordSavedNotice();
          }
          return result;
        });
      } catch (caught) {
        clearPasswordSavedNotice();
        // An older backend still wants the temporary password: remember it
        // across the form's remount so the field appears (B7 / A6).
        if (
          caught instanceof FirstLoginPasswordError &&
          caught.status === 400 &&
          /currentpassword/i.test(caught.message)
        ) {
          markFirstPasswordNeedsCurrent();
        }
        const current = getPrivateSessionState();
        if (!current.changing && current.generation === generation &&
          current.actorId === owner.actorId && current.storeSlug === owner.storeSlug) {
          client.setQueryData(["first-password-feedback", generation], true);
        }
        throw caught;
      }
    },
  });
  return { ...mutation, isPending: mutation.isPending || session.changing, replacementFailed };
}
